import {
    type CleartextMessage,
    type Key,
    readCleartextMessage,
    readKey,
    type VerifyMessageResult,
    verify,
    // @ts-ignore
} from '../../../../node_modules/openpgp/dist/openpgp.mjs';
// TODO: revert back to 'openpgp' once the issue has been resolved here: https://github.com/openpgpjs/openpgpjs/issues/1814
import type { Logger } from '../logger/Logger';
import { atob } from './LicenseManagerUtils';

/**
 * Checks if a value is missing or empty.
 *
 * @param {string | undefined} value - The value to check
 * @returns {boolean} True if the value is null, undefined, or empty string
 */
const missingOrEmpty = (value?: string | undefined): boolean => {
    return value == null || value.length === 0;
};

declare global {
    const __RELEASE_INFORMATION__: string;
    const __DISTRIBUTED_CACHING_MECHANISM__: string;
    const __MULTI_THREAD_SYNC_MUTEX__: string;
    const __INTERFACE_COMPOSITION_PATTERN__: string;
}

/** Type representing different license types */
type LicenseType = 'none' | 'free' | 'trial' | 'pro';

/**
 * The LicenseManager class is responsible for validating license keys,
 * ensuring that only users with valid licenses can access the
 * Pro version features of the FIXParsers.
 *
 * @public
 */
class LicenseManager {
    /** Release information for version checking */
    static readonly #RELEASE_INFORMATION: string | undefined =
        __RELEASE_INFORMATION__ || process.env.__RELEASE_INFORMATION__;
    /** Current license key */
    static #licenseKey: string;
    /** Whether to use distributed key */
    static #useDistributedKey = false;
    /** License key ID */
    static #licenseKeyId: string | undefined = undefined;
    /** License expiry date */
    // static #licenseExpiry: string | undefined = undefined;
    /** Current license type */
    static #licenseType: LicenseType | undefined = 'none';
    /** Logger instance */
    static #logger: Logger;
    /** Public key for license verification */
    static readonly #PUBLIC_KEY = `-----BEGIN PGP PUBLIC KEY BLOCK-----

xjMEYSAlmRYJKwYBBAHaRw8BAQdATxsL8ZGu79iIXGoMwAGxis0Ot6zN+c+H
FiEfymdU5QHNIGZpeHBhcnNlci5pbyA8aW5mb0BmaXhwYXJzZXIuaW8+wowE
EBYKAB0FAmEgJZkECwkHCAMVCAoEFgACAQIZAQIbAwIeAQAhCRDpKZsYDAPF
cxYhBOT5iMsY4w3omYDb+ukpmxgMA8VziRIA/A5mUMldqdrKsxJddLiMfJ30
DVyKt8dXn6Fu6b2riwrHAP0YqK+goCIt7y6de9KTWmnWBgMAxX5XNAK5B41A
DsfYCM44BGEgJZkSCisGAQQBl1UBBQEBB0A0y8VUtHP6LESsJZN7yEpOKHtR
2JCLu/swlNU2QfojLwMBCAfCeAQYFggACQUCYSAlmQIbDAAhCRDpKZsYDAPF
cxYhBOT5iMsY4w3omYDb+ukpmxgMA8VzB/QBAOzjjWDBPlSDYpPMv13s8OS6
Tzi6Zidom0ZY6lkJMBNCAQCNwsJXAWbxHmHmMJq5yLe5GaL9YQWxeUM4AdUa
GqHtBQ===YpfV
-----END PGP PUBLIC KEY BLOCK-----`;
    /** Distributed key for license verification */
    static readonly #DISTRIBUTED_KEY = `-----BEGIN PGP PUBLIC KEY BLOCK-----

xjMEZ9meoxYJKwYBBAHaRw8BAQdADNVkwzUzMTGsNiaL9OaegU3jLfcsK1Is
lrNhtrRgJ33NI2ZpeHBhcnNlci5kZXYgPHNhbGVzQGZpeHBhcnNlci5kZXY+
wsATBBMWCgCFBYJn2Z6jAwsJBwmQInDc59hOxMlFFAAAAAAAHAAgc2FsdEBu
b3RhdGlvbnMub3BlbnBncGpzLm9yZ45nyD2GOyHqU5VNizLnOPzwVFoSPXY+
6JXj4LrD1+YYBRUKCA4MBBYAAgECGQECmwMCHgEWIQQlYFgoCSDbZl/nmYIi
cNzn2E7EyQAAYZ4A+wfqPQX+WAViaMo4JtzcUEsQ1u4rQ146N6wKrfeilarU
AQCkXUlyDfpiXcx3N0ZMq0ZwCid55LU9NxY/bOs558/MDM44BGfZnqMSCisG
AQQBl1UBBQEBB0DnPdp7/ixyH+W2Jmtv4IMex1Eg4wWiK4uihuS0czVZFQMB
CAfCvgQYFgoAcAWCZ9meowmQInDc59hOxMlFFAAAAAAAHAAgc2FsdEBub3Rh
dGlvbnMub3BlbnBncGpzLm9yZ7Sxnb0o/uRph2KO9FVFdM7qAP9V3YKQUesV
uhbuzS/BApsMFiEEJWBYKAkg22Zf55mCInDc59hOxMkAAJ1aAP9YutddNVta
S5ZXnD0TGixaxWGBs96860f4U3qluhTjUAD9EAu8DxxpKVw59ngh8836nlRZ
7HsZg95LnBYkoMOKNQ0=
=O4nQ
-----END PGP PUBLIC KEY BLOCK-----`;

    /** Error message for missing free license */
    public static readonly ERROR_MESSAGE_NO_FREE_LICENSE = 'Requires FIXParser Free license';
    /** Error message for missing pro license */
    public static readonly ERROR_MESSAGE_NO_LICENSE = 'Requires FIXParser Pro license';

    /**
     * Private constructor to prevent instantiation.
     * This is a static class.
     */
    private constructor() {
        // Private constructor prevents instantiation
    }

    /**
     * Validates if a free license is present and valid.
     * Checks for free, pro, or trial license types.
     *
     * @returns {boolean} True if a valid free license is present
     */
    public static readonly validateFreeLicense: () => boolean = Object.freeze(() => {
        if (
            LicenseManager.#licenseKeyId &&
            (LicenseManager.#licenseType === 'free' ||
                LicenseManager.#licenseType === 'pro' ||
                LicenseManager.#licenseType === 'trial')
        ) {
            return true;
        }
        LicenseManager.outputInvalidFreeLicenseKey();
        return false;
    });

    /**
     * Validates if a pro license is present and valid.
     * Checks for pro or trial license types.
     *
     * @returns {boolean} True if a valid pro license is present
     */
    public static readonly validateLicense: () => boolean = Object.freeze(() => {
        if (
            LicenseManager.#licenseKeyId &&
            (LicenseManager.#licenseType === 'pro' || LicenseManager.#licenseType === 'trial')
        ) {
            return true;
        }
        LicenseManager.outputInvalidLicenseKey();
        return false;
    });

    /**
     * Sets the logger instance for license validation messages.
     *
     * @param {Logger} loggerInstance - The logger instance to use
     */
    public static setLogger(loggerInstance: Logger) {
        LicenseManager.#logger = loggerInstance;
    }

    /**
     * Sets and validates a license key.
     * Processes the key, verifies its signature, and extracts license information.
     *
     * @param {string} rawLicenseKey - The raw license key to validate
     * @returns {Promise<void>} A promise that resolves when the license is validated
     */
    public static async setLicenseKey(rawLicenseKey: string): Promise<void> {
        let licenseKey = `${rawLicenseKey}`.trim();
        licenseKey = licenseKey.replace(/[\x00-\x1F\x7F\x80-\x9F]/g, '');

        if (!__DISTRIBUTED_CACHING_MECHANISM__ || !__MULTI_THREAD_SYNC_MUTEX__ || !__INTERFACE_COMPOSITION_PATTERN__) {
            return;
        }

        if (`${licenseKey}`.startsWith(__DISTRIBUTED_CACHING_MECHANISM__)) {
            licenseKey = licenseKey.replace(__DISTRIBUTED_CACHING_MECHANISM__, '');
            LicenseManager.#useDistributedKey = true;
        }

        if (
            !(
                `${licenseKey}`.startsWith(__MULTI_THREAD_SYNC_MUTEX__) ||
                `${licenseKey}`.startsWith(__INTERFACE_COMPOSITION_PATTERN__)
            )
        ) {
            LicenseManager.outputInvalidLicenseKey();
            return;
        }

        LicenseManager.#licenseKey = licenseKey;

        if (missingOrEmpty(LicenseManager.#licenseKey)) {
            LicenseManager.outputMissingLicenseKey();
        } else {
            try {
                const readPublicKey: Key = await readKey({
                    armoredKey: LicenseManager.#useDistributedKey
                        ? LicenseManager.#DISTRIBUTED_KEY
                        : LicenseManager.#PUBLIC_KEY,
                });
                const signedMessage: CleartextMessage = await readCleartextMessage({
                    cleartextMessage: atob(LicenseManager.#licenseKey)!,
                });
                const verificationResult: VerifyMessageResult = await verify({
                    message: signedMessage as any,
                    verificationKeys: readPublicKey,
                });
                const { verified, keyID } = verificationResult.signatures[0];
                await verified;
                const [, , expiryTimestamp, licenseTypeValue]: string[] = signedMessage.getText().split('|');

                let licenseType: LicenseType = 'none';
                if (licenseTypeValue === 'free') {
                    licenseType = 'free';
                } else if (licenseTypeValue === 'trial') {
                    licenseType = 'trial';
                } else if (licenseTypeValue === 'pro') {
                    licenseType = 'pro';
                } else if (licenseTypeValue === 'true') {
                    // Legacy
                    licenseType = 'trial';
                } else if (licenseTypeValue === 'false') {
                    // Legacy
                    licenseType = 'pro';
                } else {
                    LicenseManager.outputInvalidFreeLicenseKey();
                }

                LicenseManager.validateKey(keyID.toHex(), Number(expiryTimestamp), licenseType);
            } catch (_error) {
                LicenseManager.outputInvalidLicenseKey();
                return;
            }
        }
        return Promise.resolve();
    }

    /**
     * Gets the release date from the release information.
     *
     * @returns {Date} The release date
     */
    private static getReleaseDate(): Date {
        return new Date(Number.parseInt(atob(LicenseManager.#RELEASE_INFORMATION!)!, 10));
    }

    /**
     * Formats a date into a human-readable string.
     *
     * @param {Date} date - The date to format
     * @returns {string} The formatted date string
     */
    private static formatDate(date: Date): string {
        const monthNames: string[] = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];

        const day: number = date.getDate();
        const monthIndex: number = date.getMonth();
        const year: number = date.getFullYear();

        return `${day} ${monthNames[monthIndex]} ${year}`;
    }

    /**
     * Validates a license key by checking its ID, expiry date, and type.
     *
     * @param {string} keyId - The license key ID
     * @param {number} expiryTimestamp - The license expiry timestamp
     * @param {LicenseType} type - The license type
     * @returns {boolean} True if the license is valid
     */
    private static validateKey(keyId: string, expiryTimestamp: number, type: LicenseType): boolean {
        const releaseDate: Date = LicenseManager.getReleaseDate();
        const expiry: Date = new Date(expiryTimestamp);

        let valid = false;
        let current = false;
        if (!Number.isNaN(expiry.getTime())) {
            valid = true;
            current = releaseDate < expiry;
        }

        if (!valid) {
            if (type === 'free') {
                LicenseManager.outputInvalidFreeLicenseKey();
            } else {
                LicenseManager.outputInvalidLicenseKey();
            }
            return false;
        }
        if (!current) {
            const formattedExpiryDate: string = LicenseManager.formatDate(expiry);
            const formattedReleaseDate: string = LicenseManager.formatDate(releaseDate);
            LicenseManager.outputIncompatibleVersion(formattedExpiryDate, formattedReleaseDate);
            return false;
        }
        LicenseManager.#licenseKeyId = keyId;
        // LicenseManager.#licenseExpiry = LicenseManager.formatDate(expiry);
        LicenseManager.#licenseType = type;
        if (type === 'free') {
            LicenseManager.outputValidFreeLicense();
        } else {
            LicenseManager.outputValidLicense();
        }
        return true;
    }

    /**
     * Outputs a message indicating a valid free license.
     */
    private static outputValidFreeLicense(): void {
        // const licenseMessage = `[FIXParser Free License] - Valid until ${LicenseManager.#licenseExpiry}`;
        // if (LicenseManager.#logger) {
        //     LicenseManager.#logger.log({
        //         level: 'info',
        //         message: licenseMessage,
        //     });
        // }
    }

    /**
     * Outputs a message indicating a valid pro license.
     */
    private static outputValidLicense(): void {
        // const licenseMessage = `[FIXParser Pro License] - Valid until ${LicenseManager.#licenseExpiry}`;
        // if (LicenseManager.#logger) {
        //     LicenseManager.#logger.log({
        //         level: 'info',
        //         message: licenseMessage,
        //     });
        // }
    }

    /**
     * Outputs a message indicating an invalid free license.
     */
    private static outputInvalidFreeLicenseKey(): void {
        const licenseMessage = `
****************************************************************************************************
************************************* FIXParser Free License ***************************************
***************************************** Invalid License ******************************************
*              -- Access to this feature requires a free license key for FIXParser --              *
*                      Your license for FIXParser is not valid - please visit                      *
*                  https://fixparser.dev/register to register for a free license.                  *
****************************************************************************************************
****************************************************************************************************
`;
        if (LicenseManager.#logger) {
            LicenseManager.#logger.log({
                level: 'error',
                message: licenseMessage,
            });
        } else {
            console.error(licenseMessage);
        }
    }

    /**
     * Outputs a message indicating an invalid pro license.
     */
    private static outputInvalidLicenseKey(): void {
        const licenseMessage = `
****************************************************************************************************
************************************** FIXParser Pro License ***************************************
***************************************** Invalid License ******************************************
*                  -- Access to this feature requires license for FIXParser Pro --                 *
*        Your license for FIXParser Pro is not valid - please contact sales@fixparser.dev to       *
*                                     obtain a valid license.                                      *
****************************************************************************************************
****************************************************************************************************
`;
        if (LicenseManager.#logger) {
            LicenseManager.#logger.log({
                level: 'error',
                message: licenseMessage,
            });
        } else {
            console.error(licenseMessage);
        }
    }

    /**
     * Outputs a message indicating a missing license key.
     */
    private static outputMissingLicenseKey(): void {
        const licenseMessage = `
****************************************************************************************************
***************************************** FIXParser License ****************************************
*************************************** License Key Not Found **************************************
*     Please visit https://fixparser.dev to request a free license, or purchase FIXParser Pro.     *
****************************************************************************************************
****************************************************************************************************
`;
        if (LicenseManager.#logger) {
            LicenseManager.#logger.log({
                level: 'error',
                message: licenseMessage,
            });
        } else {
            console.error(licenseMessage);
        }
    }

    /**
     * Outputs a message indicating an incompatible version.
     *
     * @param {string} formattedExpiryDate - The formatted expiry date
     * @param {string} formattedReleaseDate - The formatted release date
     */
    private static outputIncompatibleVersion(formattedExpiryDate: string, formattedReleaseDate: string): void {
        const padStringRight = (str: string, length: number): string => {
            return str.padEnd(length, ' ');
        };
        const licenseMessage = `
****************************************************************************************************
****************************************************************************************************
*                    License not compatible with installed version of FIXParser.                   *
*                                                                                                  *
* Your FIXParser License entitles you to all versions of FIXParser that we release within the time *
* covered by your license - typically we provide one year licenses which entitles you to all       *
* releases / updates of FIXParser within that year. Your license has an end (expiry) date which    *
* stops the license key working with versions of FIXParser released after the license end date.    *
* The license key that you have expires on ${padStringRight(`${formattedExpiryDate}, however, the version of FIXParser`, 56)}*
* you are trying to use was released on ${padStringRight(`${formattedReleaseDate}.`, 59)}*
*                                                                                                  *
* Please visit https://fixparser.dev to renew your subscription to new versions and get a new      *
* license key to work with this version of FIXParser.                                              *
****************************************************************************************************
****************************************************************************************************
`;
        if (LicenseManager.#logger) {
            LicenseManager.#logger.log({
                level: 'error',
                message: licenseMessage,
            });
        } else {
            console.error(licenseMessage);
        }
    }
}

// Prevent LicenseManager.validateLicense from being modified
Object.defineProperty(LicenseManager, 'validateLicense', {
    writable: false,
    configurable: false,
});

export { LicenseManager };
